import { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import cn from 'classnames';
import { AddonGroup } from 'models/AddonGroup';
import { ProductPageModel } from 'models/ProductPageModel';
import { hasElements, NO_ELEMENT, rm } from 'utils';
import { getQaDataAttr } from '@bygghemmait/utilities';
import { IconArrow } from 'components/Shared/Icon/themes/BGH-RD3/iconArrow';
import { Grid, Cell } from 'components/Grid';
import { Button } from 'components/Button';
import {
  AddonActionPayload,
  selectAddon as _selectAddon,
  unselectAddon as _unselectAddon
} from 'views/ProductPage/addons/actions';
import { getAddons, isFamily } from 'views/ProductPage/addons/selectors';
import { convertObjectToArray } from 'utils/convertObjectToArray';
import accordionStyles from 'components/Accordion/styles.scss';
import styles from './style.scss';
import {
  isMobileLayout,
  isLarge,
  isMobile as isMobileSelector
} from 'components/Shared/responsive';
import { getFullProductId, getVariantId } from 'views/ProductPage/selector';
import { PageState } from 'models/PageState';
import { useIntersectionObserver, usePrevious } from 'hooks';
import { TrackedElement } from 'hooks/useIntersectionObserver';
import { GtmViewGroupAddon } from 'store/googleTagManager/actions';
import { GroupAddon } from 'views/ProductPage/addons/themes/BGH/groupAddon';
import { SingleAddon } from 'views/ProductPage/addons/themes/BGH/singleAddon';
import { getCategoryDisplayNameWithUppercase } from './utils';

const NBR_OF_VISIBLE_SELECTED_ADDONS = 3;

export type ToggleAddon = (payload: AddonActionPayload, selected: boolean) => void;
export type Addon = AddonGroup & { key: string };

type Addons = ProductPageModel['addons'];

export type AddonProps = {
  toggleAddon: ToggleAddon;
  addon: Addon;
  isMobile?: boolean;
};

const hasAddons = (addons?: Addons): addons is NonNullable<Addons> =>
  addons !== undefined && hasElements(Object.keys(addons));

type _AddonsProps = {
  canSelectAddons: boolean;
  smallGutters: boolean;
  toggleAddon: ToggleAddon;
  variantId: string | undefined;
  addons?: Addons;
  isMobile?: boolean;
  className?: string;
};

export const _Addons = ({
  addons,
  canSelectAddons,
  className,
  isMobile,
  smallGutters,
  toggleAddon,
  variantId
}: _AddonsProps) => {
  const dispatch = useDispatch();
  const [isOpen, setIsOpen] = useState(canSelectAddons);
  const toggleOpen = () => canSelectAddons && setIsOpen(!isOpen);
  const hasVariantId = !!variantId;
  const hadVariantId = usePrevious(hasVariantId);
  const productId = useSelector((state: PageState) => getFullProductId(state.currentPage));

  const { setElementRef } = useIntersectionObserver({
    onBatchVisible: handleElementsVisible
  });

  function handleElementsVisible(elements: TrackedElement<Addon>[]) {
    const viewedGroupAddons = elements.map(element => {
      const addon = element.payload;
      return addon.displayName;
    });

    dispatch(GtmViewGroupAddon(viewedGroupAddons, productId));
  }

  useEffect(() => {
    if (!hadVariantId && hasVariantId && canSelectAddons) {
      setIsOpen(true);
    }
  }, [hadVariantId, hasVariantId, canSelectAddons]);

  if (!hasAddons(addons)) {
    return NO_ELEMENT;
  }

  const addonList = convertObjectToArray(addons);
  const singleChoiceAddons = addonList.filter(addon => addon.products.length === 1);
  const multiChoiceAddons = addonList.filter(addon => addon.products.length > 1);
  const sortedAddons = [...singleChoiceAddons, ...multiChoiceAddons];

  const selectedAddons = sortedAddons
    .filter(addon => addon.products.some(product => product.selected))
    .map(addon => getCategoryDisplayNameWithUppercase({ name: addon.displayName }));

  const selectedAddonsToShow = selectedAddons.slice(0, NBR_OF_VISIBLE_SELECTED_ADDONS).join(', ');
  const nbrOfHiddenSelectedAddons = selectedAddons.length - NBR_OF_VISIBLE_SELECTED_ADDONS;
  const hasHiddenSelectedAddons = nbrOfHiddenSelectedAddons > 0;

  const titleCaption = canSelectAddons
    ? 'product_red_addons_header'
    : 'product_red_addons_header_inactive';

  const numberOfColumns = isMobile ? '1' : '2';

  return (
    <div
      className={cn(
        styles.addons,
        {
          [styles.mobile]: isMobile
        },
        className
      )}
    >
      <Button
        disabled={!canSelectAddons}
        onClick={toggleOpen}
        appearance="tertiary"
        className={cn(styles.header, accordionStyles.container)}
      >
        <h4 className={cn(styles.title, accordionStyles.sectionHeader)}>{rm(titleCaption)}</h4>
        {!isOpen && (
          <span className={styles.choices}>
            {selectedAddonsToShow}
            {hasHiddenSelectedAddons && `, +${nbrOfHiddenSelectedAddons}`}
          </span>
        )}
        <IconArrow
          className={styles.arrow}
          size="tiny"
          appearance={isOpen ? 'flip-270' : 'flip-90'}
        />
      </Button>
      {isOpen && canSelectAddons && (
        <Grid
          tag="ul"
          className={styles.grid}
          phone="1"
          tablet="1"
          large="1"
          desktop={numberOfColumns}
          huge={numberOfColumns}
          ultra={numberOfColumns}
          gutters={smallGutters ? 'medium' : 'extraLarge'}
        >
          {sortedAddons.map(addon => (
            <Cell tag="li" key={addon.key} {...getQaDataAttr(`AddonButton`)}>
              {addon.products.length === 1 && multiChoiceAddons.length === 0 ? (
                <SingleAddon toggleAddon={toggleAddon} addon={addon} isMobile={isMobile} />
              ) : (
                <GroupAddon
                  toggleAddon={toggleAddon}
                  addon={addon}
                  isMobile={isMobile}
                  ref={node => setElementRef({ ref: node, payload: addon })}
                />
              )}
            </Cell>
          ))}
        </Grid>
      )}
    </div>
  );
};

type AddonsProps = {
  className?: string;
};

export const Addons = ({ className }: AddonsProps) => {
  const dispatch = useDispatch();
  const addons = useSelector(getAddons);
  const canSelectAddons = !useSelector(isFamily);

  const isMobile = useSelector(isMobileSelector);
  const mobileOrTablet = useSelector(isMobileLayout);
  const large = useSelector(isLarge);
  const smallGutters = mobileOrTablet || large;

  const variantId = useSelector(({ currentPage }: PageState) => getVariantId(currentPage));

  const toggleAddon: ToggleAddon = (payload, selected) => {
    if (selected) {
      dispatch(_unselectAddon(payload));
    } else {
      dispatch(_selectAddon(payload));
    }
  };

  return (
    <_Addons
      addons={addons}
      canSelectAddons={canSelectAddons}
      className={className}
      isMobile={isMobile}
      smallGutters={smallGutters}
      toggleAddon={toggleAddon}
      variantId={variantId}
    />
  );
};
